import { useEffect, useRef, useState } from 'react';
import {
  Platform,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
  type ViewStyle,
} from 'react-native';
import { Box, HStack, Text } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useTranslatedStageName } from '../../../hooks/useTranslatedStageName';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionTypography } from '../../../theme/typography';
import type { BoardStage, Lead } from '../../../types/leads';
import { useDragContext } from './DragContext';
import { LeadCard } from './LeadCard';

interface KanbanColumnProps {
  stage: BoardStage;
  columnWidth?: number;
  dragEnabled?: boolean;
  onLeadPress?: (lead: Lead) => void;
}

export function KanbanColumn({
  stage,
  columnWidth = 300,
  dragEnabled = true,
  onLeadPress,
}: Readonly<KanbanColumnProps>) {
  const { t } = useTranslation();
  const translateStageName = useTranslatedStageName();
  const { width: viewportWidth } = useWindowDimensions();
  const isMobile = viewportWidth < tokens.breakpoints.tablet;
  const { draggingLead, sourceStageId, hoverStageId, setHoverStage, dropOnStage } =
    useDragContext();
  const [dragOver, setDragOver] = useState(false);
  const dropRef = useRef<View>(null);

  const isDropTarget = dragEnabled && !!draggingLead && sourceStageId !== stage.id;
  const isHighlighted = dragOver || hoverStageId === stage.id;

  const setHoverStageRef = useRef(setHoverStage);
  setHoverStageRef.current = setHoverStage;
  const dropOnStageRef = useRef(dropOnStage);
  dropOnStageRef.current = dropOnStage;
  const stageIdRef = useRef(stage.id);
  stageIdRef.current = stage.id;
  const isDropTargetRef = useRef(isDropTarget);
  isDropTargetRef.current = isDropTarget;

  useEffect(() => {
    if (!dragEnabled || Platform.OS !== 'web' || !dropRef.current) return;

    const node = dropRef.current as unknown as HTMLElement;
    if (!node?.addEventListener) return;

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'move';
      }
      if (isDropTargetRef.current) {
        setDragOver(true);
        setHoverStageRef.current(stageIdRef.current);
      }
    };

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      if (isDropTargetRef.current) {
        setDragOver(true);
        setHoverStageRef.current(stageIdRef.current);
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      if (relatedTarget && node.contains(relatedTarget)) return;
      setDragOver(false);
      setHoverStageRef.current(null);
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      dropOnStageRef.current(stageIdRef.current);
    };

    node.addEventListener('dragover', handleDragOver);
    node.addEventListener('dragenter', handleDragEnter);
    node.addEventListener('dragleave', handleDragLeave);
    node.addEventListener('drop', handleDrop);

    return () => {
      node.removeEventListener('dragover', handleDragOver);
      node.removeEventListener('dragenter', handleDragEnter);
      node.removeEventListener('dragleave', handleDragLeave);
      node.removeEventListener('drop', handleDrop);
    };
  }, [dragEnabled]);

  const webTransition = Platform.select({
    web: {
      transition: 'border-color 0.2s ease, background-color 0.2s ease',
    } as ViewStyle,
    default: {},
  });

  // A column is a lane, not a card. It carries no border, no fill and no
  // shadow, because the lead cards inside it are the bordered layer — a shell
  // around them stacks a card inside a card. Lanes are told apart by the gap
  // between them and by their header, not by chrome. While a lead is being
  // dragged the target lane lifts a teal wash so the drop zone is unmistakable;
  // that is the one moment a lane paints anything at all.
  const highlighted = isDropTarget && isHighlighted;

  const columnContent = (
    <Box
      testID={`kanban_column_${stage.id}`}
      backgroundColor={highlighted ? colors.primary.faint : 'transparent'}
      borderRadius={highlighted ? 'lg' : 'none'}
      style={{
        // Mobile: fill the parent (vertical-stacked columns). Using a numeric
        // width here is fragile because useWindowDimensions().width is the FULL
        // viewport, while the actual parent has paddingHorizontal applied by
        // DashboardLayout.mobileContentPadding — the difference (16+ px on
        // iPhone) is exactly what was getting clipped on the right edge.
        // Desktop/tablet keep numeric widths because columns scroll horizontally.
        width: isMobile ? '100%' : columnWidth,
        maxHeight: '100%',
        ...webTransition,
      }}
    >
      {/* Eyebrow, dot and count — the same label treatment used everywhere else
          in the product. The stage colour rides the dot so the hue is always
          paired with a shape, and the count is mono.

          The count sits next to its label rather than flush to the lane's far
          edge. Once the lane lost its shell there is no border to anchor a
          right-aligned number to, and at 300px it lands nearer the next lane's
          label than its own — reading as that lane's count. */}
      <HStack spacing="sm" align="center" style={styles.header}>
        <Box width={6} height={6} borderRadius="full" backgroundColor={stage.color} />
        <Text
          testID={`kanban_column_${stage.id}_name_text`}
          style={darkPrecisionTypography.label}
          numberOfLines={1}
        >
          {translateStageName(stage.name)}
        </Text>
        <Text
          testID={`kanban_column_${stage.id}_count_text`}
          style={[darkPrecisionTypography.metricSm, styles.count]}
        >
          {stage.count}
        </Text>
      </HStack>

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={styles.laneContent}
        showsVerticalScrollIndicator={false}
      >
        {isDropTarget && isHighlighted && (
          <Box borderRadius="mdPlus" padding="md" alignItems="center" style={styles.dropZone}>
            <Text style={darkPrecisionTypography.label} color={colors.primary[300]}>
              {t('leads.kanban.dropHere')}
            </Text>
          </Box>
        )}

        {stage.leads.length === 0 && !isHighlighted ? (
          // An empty lane says so once, quietly. It gets no placeholder box:
          // the whole point of the lane is that only real cards draw an edge.
          <Box paddingY="xl" alignItems="center">
            <Text variant="caption" color={colors.text.tertiary}>
              {t('leads.kanban.noLeads')}
            </Text>
          </Box>
        ) : (
          // No VStack wrapper: on iOS, a VStack without width hint takes
          // intrinsic width from its children, and the cards' alignSelf:'stretch'
          // ends up stretching against an unbounded reference — letting the
          // cards overflow the column. Rendering directly inside the ScrollView's
          // contentContainer gives the cards a definite parent width (= columnWidth).
          stage.leads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              stageId={stage.id}
              stageColor={stage.color}
              stageName={stage.name}
              dragEnabled={dragEnabled}
              onPress={onLeadPress}
            />
          ))
        )}
      </ScrollView>
    </Box>
  );

  if (dragEnabled && Platform.OS === 'web') {
    return (
      <View ref={dropRef} style={{ maxHeight: '100%' }}>
        {columnContent}
      </View>
    );
  }

  return columnContent;
}

const CARD_GUTTER = tokens.spacing.sm;

const styles = StyleSheet.create({
  header: {
    // Matches the lane's card gutter so the eyebrow starts on the same
    // vertical line as the card edges below it.
    paddingHorizontal: CARD_GUTTER,
    paddingTop: tokens.spacing.xs,
    paddingBottom: tokens.spacing.md,
  },
  count: {
    color: colors.text.tertiary,
    fontVariant: ['tabular-nums'],
  },
  laneContent: {
    paddingHorizontal: CARD_GUTTER,
    paddingBottom: CARD_GUTTER,
    gap: tokens.spacing.smPlus,
  },
  dropZone: {
    backgroundColor: colors.primary.muted,
  },
});
